/**
 * BoardConversationDrawer — the agent conversation as a COLLAPSIBLE tenant of a
 * board-first surface, rather than a permanent fraction of it.
 *
 * Why this exists, measured on staging (21 August 2026, care-planner, a real
 * 3-day plan with 5 disciplines): the board's grid needs 1333px. The strip below
 * it took 229px of a 654px pane, the composer 67px, and the board's own header
 * another 122px — so the nurse saw 236px of grid, less than ONE of five
 * discipline rows. Capping the strip (45vh → 35% of the pane) fixed the
 * arithmetic and changed nothing she experiences: no fraction of a 654px pane
 * makes a 1333px board the main interface. The strip had to stop being a
 * permanent tenant.
 *
 * Collapsed, it costs one row. The row is not a placeholder: it PEEKS the
 * agent's latest sentence, because the reason BoardAgentChat exists at all is
 * that board-first agents answer on the async-turn path and their prose replies
 * were being swallowed (see that file's header). A drawer that hid the reply
 * behind a chevron would re-introduce the swallow this component is downstream
 * of, so the peek carries the words and the chevron carries the thread.
 *
 * It opens ITSELF for anything the nurse has not read yet: a new assistant
 * reply that arrives while she is on the surface, a turn in flight, or a send
 * error. What it does not do is re-open for the reply that was already there
 * when she arrived — that one is history, and the plan is what she came for.
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown } from 'lucide-react'

import { conversationText, type MessageResponse } from '@/api/types_session'
import type { SessionDetail } from '@/hooks/useSessions'
import { BoardAgentChat } from './BoardAgentChat'
import { usePendingTurnsStore, pendingTurnsForSession } from '@/stores/pendingTurns'
import type { useStreamingMessage } from '@/hooks/useStreamingChat'

/**
 * The agent's most recent message that actually says something in words.
 * Assistant messages with empty content exist only to carry a board block (the
 * board draws those), so they are not a sentence to peek.
 */
export function latestAgentProse(messages: MessageResponse[]): MessageResponse | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.role === 'agent' && conversationText(m.payload).trim().length > 0) return m
  }
  return null
}

export function BoardConversationDrawer({
  session,
  streaming,
}: {
  session: SessionDetail
  streaming: ReturnType<typeof useStreamingMessage>
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const latest = useMemo(() => latestAgentProse(session.messages.items), [session.messages])

  // The reply that was already on screen when this mounted is history; only a
  // reply that ARRIVES opens the drawer. Seeding the ref on the first run is
  // what draws that line — without it, every open of an existing plan would
  // pop the conversation over the board again.
  //
  // Keyed on the SESSION, not on mount: the satellite page routes
  // /sessions/:id without a `key`, so switching from one patient's plan to
  // another reuses this instance. Comparing patient A's last reply id against
  // patient B's would differ every time and pop the drawer open on arrival —
  // exactly the behaviour the seeding above exists to prevent.
  const seenReply = useRef<{ sessionId: string; replyId: string } | null>(null)
  useEffect(() => {
    const id = latest?.id ?? ''
    const seen = seenReply.current
    if (!seen || seen.sessionId !== session.session.id) {
      seenReply.current = { sessionId: session.session.id, replyId: id }
      return
    }
    if (id && seen.replyId !== id) {
      seenReply.current = { sessionId: session.session.id, replyId: id }
      setOpen(true)
    }
  }, [latest, session.session.id])

  // A turn in flight and a failed send are both things she is waiting on, so
  // they open the drawer. This SETS the state rather than overriding it at
  // render time: an override would leave the chevron inert while it lasted —
  // her click would flip a hidden boolean, and the drawer would snap to that
  // unseen value the moment the turn ended. Opening it for real means she can
  // always close it again, and closing it is then her decision to keep.
  //
  // "In flight" is the PENDING TURN, not `streaming.isStreaming`. Board-first
  // agents answer on the async path, where the server returns 202 immediately
  // and useStreamingChat clears isStreaming right there (see its comment at the
  // setState after registerTurn) — so isStreaming covers the send, not the
  // work. Keying on it would open the drawer for the handful of milliseconds
  // before the 202 and leave it shut for the seconds the agent actually thinks.
  // isRetrying and error stay: those are that same pre-202 send failing, which
  // she also has to see.
  const pendingTurns = usePendingTurnsStore((s) => s.turns)
  const turnInFlight = pendingTurnsForSession(pendingTurns, session.session.id).length > 0
  useEffect(() => {
    if (turnInFlight || streaming.isRetrying || streaming.error) setOpen(true)
  }, [turnInFlight, streaming.isRetrying, streaming.error])

  const transcriptId = useId()
  const peek = latest ? conversationText(latest.payload).trim() : ''

  return (
    // The cap lives HERE, on the drawer, not on the transcript inside it: a
    // percentage max-height resolves against the nearest DEFINITE height, and
    // the drawer's own height is content-driven. `max-h-[50%]` on the transcript
    // would therefore resolve to nothing and the conversation would push the
    // board off-screen the moment it grew — the bug this component replaces,
    // reintroduced one level down. The pane IS definite (h-full inside Home's
    // `flex-1 min-h-0` box, h-dvh in the satellite page), so the cap binds here.
    <div className="flex max-h-[50%] shrink-0 flex-col border-t border-[var(--color-surface-border)] bg-[var(--color-surface-bg)]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={transcriptId}
        className="flex min-h-11 w-full shrink-0 items-center gap-2 px-4 py-2 text-left transition active:scale-[0.99]"
      >
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-[var(--color-surface-mute)] transition-transform ${open ? '' : '-rotate-90'}`}
          aria-hidden="true"
        />
        <span className="min-w-0 flex-1 truncate text-[13px] text-[var(--color-surface-mute)]">
          {peek || t('chat.conversation', 'Conversation')}
        </span>
      </button>
      {open && (
        <BoardAgentChat
          id={transcriptId}
          session={session}
          streaming={streaming}
          className="min-h-0 flex-1 overflow-y-auto px-4 pb-2"
        />
      )}
    </div>
  )
}
